/// <amd-module name="Repos/Views/PullRequest/Components/ActivityFeed/ActivityFeed" />
import * as React from "react";
import { CommentThreadStatus, GitPullRequestCommentThread, IterationReason } from "Repos/Common/Contracts/Git";
import {
    getPullRequestDiscussionService,
    IGitPullRequestCommentThreadModel
} from "Repos/Views/PullRequest/Discussion/PullRequestDiscussionThreadService";
import { IPullRequestViewService } from "Repos/Views/PullRequest/PullRequestViewService";
import * as Resources from "Repos/Views/PullRequest/Resources";
import { IObservableValue, IReadonlyObservableArray, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { equals, format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { Dropdown, DropdownExpandableButton } from "VSSUI/Dropdown";
import { Icon } from "VSSUI/Icon";
import { List } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { TextField } from "VSSUI/TextField";
import { Timeline, TimelineSequence } from "VSSUI/Timeline";
import { css } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { getActivityFeedShimService } from "../../ActivityFeedShimService";
import { IIteration } from "../../Contracts/PullRequest";
import { hasPermission, PullRequestPermissions } from "../../PullRequestPermissionService";
import { ActivityFeedIntersectionRow } from "./ActivityFeedIntersectionRow";
import { getPropertyValue } from "./Utils/ActivityFeedUtils";

export enum DiscussionIds {
    Unknown = "",
    Comment = "comment-activity-card",
    FileComment = "file-comment-activity-card",
    Merge = "merge-activity-card",
    Commit = "commit-activity-card",
    Vote = "vote-updated-activity-card",
    PREvent = "pr-event-activity-card",
    Policy = "policy-activity-card",
    CodeAnalysis = "",
    Reviewer = "reviewers-updated-activity-card",
    AutoComplete = "auto-complete-activity-card",
    Iteration = "update-activity-card",
    ResetAllVotes = "all-votes-reset-activity-card",
    ResetMultipleVotes = "multi-votes-reset-activity-card",
    IsDraft = "draft-activity-card",
    ConflictsResolved = "update-activity-card",
    BrokenState = "broken-state-card",
    SourceBranchChanged = "source-branch-changed-activity-card"
}

enum SystemDiscussionIds {
    MERGEATTEMPT = "MergeAttempt",
    REFUPDATE = "RefUpdate",
    VOTEUPDATE = "VoteUpdate",
    RESETALLVOTES = "ResetAllVotes",
    RESETMULTIPLEVOTES = "ResetMultipleVotes",
    STATUSUPDATE = "StatusUpdate",
    POLICYSTATUSUPDATE = "PolicyStatusUpdate",
    CODEANALYSISISSUE = "CodeAnalysisIssue",
    REVIEWERSUPDATE = "ReviewersUpdate",
    AUTOCOMPLETEUPDATE = "AutoCompleteUpdate",
    ISDRAFTUPDATE = "IsDraftUpdate",
    PULLREQUESTSTATUSUPDATE = "AssociatedStatusUpdate",
    TARGETCHANGED = "TargetChanged",
    CONFLICTSRESOLVED = "ConflictsResolved",
    BROKENSTATE = "BrokenState",
    SOURCEBRANCHCHANGED = "SourceBranchChanged"
}

enum FilterIds {
    Everything = "everything",
    AllComments = "all_comments",
    New = "new",
    MyComments = "my_comments",
    ActiveComments = "active_comments",
    ResolvedComments = "resolved_comments"
}

interface ActivityEntry {
    isAddCommentRow?: boolean;
    itemModel?: IGitPullRequestCommentThreadModel;
    iteration?: IIteration;
}

export class ActivityFeed extends VssComponent {
    private filters: { id: string; format: string }[] = [
        { id: FilterIds.Everything, format: Resources.ActivityFeedFilterShowEverything },
        { id: FilterIds.AllComments, format: Resources.ActivityFeedFilterAllComments },
        { id: FilterIds.New, format: Resources.ActivityFeedFilterNew },
        { id: FilterIds.MyComments, format: Resources.ActivityFeedFilterMyComments },
        { id: FilterIds.ActiveComments, format: Resources.ActivityFeedFilterActiveComments },
        { id: FilterIds.ResolvedComments, format: Resources.ActivityFeedFilterResolvedComments }
    ];

    private filterItems = new ObservableValue<IListBoxItem[]>([]);
    private newCommentRef = React.createRef<TextField>();
    private selection = new DropdownSelection();
    private selectedFilterId = new ObservableValue<string>("");

    private items: IReadonlyObservableArray<IGitPullRequestCommentThreadModel>;
    private filteredItems: ObservableArray<ActivityEntry> = new ObservableArray<ActivityEntry>([]);
    private isScrollPending: boolean;
    private iterations: IReadonlyObservableArray<IIteration>;
    private listRef = React.createRef<List<ActivityEntry>>();
    private selectedThreadId: number;
    private supportsIterations: boolean;
    private timerManagement = new TimerManagement();

    private currentUserId: string;

    private itemRefs: React.RefObject<HTMLDivElement>[] = [];
    private itemHeights: { [threadId: number]: IObservableValue<number> } = {};
    private timelineItemHeights = new ObservableValue<number[]>([]);
    private recalculateItemHeights: { [threadId: number]: boolean } = {};
    private recalculateHandlers: { [threadId: number]: number } = {};
    private debouncedHeightChanges: () => void;

    public constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        getActivityFeedShimService(this.context.pageContext).reset();

        this.debouncedHeightChanges = this.timerManagement.debounce(this.setTimelineHeights, 100);

        const discussionService = getPullRequestDiscussionService(context.pageContext);
        this.items = discussionService.getDiscussionThreads();

        const pullRequestViewService = context.pageContext.getService<IPullRequestViewService>("IPullRequestViewService");
        this.iterations = pullRequestViewService.getIterations();
        this.supportsIterations = pullRequestViewService.getIterationsSupport();
        this.selectedThreadId = pullRequestViewService.getSelectedThreadId().value || 0;
        this.isScrollPending = Boolean(this.selectedThreadId);

        const pageService = context.pageContext.getService<IVssPageService>("IVssPageService");
        this.currentUserId = pageService.getData()!.user.id;

        const filterIndex = Math.max(
            0,
            this.filters.findIndex(f => f.id === pullRequestViewService.getActivityFeedFilter())
        );
        this.selection.select(filterIndex);
        this.selectedFilterId.value = this.filters[filterIndex].id;
        this.onItemsChange();
        this.items.subscribe(this.onItemsChange);
        this.iterations.subscribe(this.onItemsChange);

        // Set focus to "Add discussion" so it's not lost when deleted discussion disappears
        const focusNewComment = () => this.newCommentRef.current?.focus();
        discussionService.addThreadRemovedListener(focusNewComment);
        this.addUnmountCallback(() => {
            discussionService.removeThreadRemovedListener(focusNewComment);
        });

        window.addEventListener("resize", this.onResize);
        this.addUnmountCallback(() => {
            window.removeEventListener("resize", this.onResize);
        });
    }

    public componentDidMount() {
        this.scrollToSelectedThread();
    }

    public componentWillUnmount() {
        this.items.unsubscribe(this.onItemsChange);
        this.iterations.unsubscribe(this.onItemsChange);
        this.timerManagement.dispose();
    }

    public render() {
        const canAddComments = hasPermission(this.context.pageContext, PullRequestPermissions.AddEditComment);
        return (
            <ScreenSizeObserver>
                {(screenProps: { screenSize: ScreenSize }) => {
                    const isLargeScreen = screenProps.screenSize >= ScreenSize.medium;
                    const pageSize = isLargeScreen ? 100 : 8; // Use a lower page size on small screens since activity items can be expensive to render

                    return (
                        <Observer filterItems={this.filterItems} timelineItemHeights={this.timelineItemHeights}>
                            {(props: { timelineItemHeights: number[] }) => {
                                return (
                                    <>
                                        <h1 className="screen-reader-only">{Resources.ActivityFeed}</h1>
                                        <Dropdown
                                            className="repos-activity-filter-dropdown padding-top-16 margin-bottom-16 margin-right-8 justify-end"
                                            items={this.filterItems.value}
                                            selection={this.selection}
                                            onSelect={this.onThreadFilterChange}
                                            renderExpandable={DropdownExpandableButton}
                                            width={250}
                                        />
                                        <Timeline
                                            className={css("activity-feed-list", canAddComments && "can-add-comments")}
                                            itemProvider={this.filteredItems}
                                            listRef={this.listRef}
                                            pageSize={pageSize}
                                            renderItem={this.renderItem}
                                            rowHeights={props.timelineItemHeights}
                                        />
                                    </>
                                );
                            }}
                        </Observer>
                    );
                }}
            </ScreenSizeObserver>
        );
    }

    private scrollToSelectedThread() {
        if (this.isScrollPending && this.listRef.current && this.items.length > 0) {
            const index = this.filteredItems.value.findIndex(item => item.itemModel?.id === this.selectedThreadId);
            if (index >= 0) {
                this.listRef.current?.scrollIntoView(index, { block: "center" });
            }
            this.isScrollPending = false;
        }
    }

    private setTimelineHeights = () => {
        let itemHeightsChanged = false;
        const itemHeights: number[] = [];

        this.filteredItems.value.forEach(item => {
            if (item.itemModel) {
                const id = item.itemModel.id;
                itemHeights.push(this.itemHeights[id]?.value || 0);
                if (!itemHeightsChanged) {
                    // check if item height has actually changed
                    const index = itemHeights.length - 1;
                    itemHeightsChanged = itemHeights[index] !== this.timelineItemHeights.value[index];
                }
            }
        });

        if (itemHeights.length === this.timelineItemHeights.value.length && !itemHeightsChanged) {
            // no changes were found, and item length is the same, so return to avoid rerender
            return;
        }

        this.timelineItemHeights.value = itemHeights;
    };

    private renderItem = (rowIndex: number, entry: ActivityEntry): [JSX.Element | null, JSX.Element | null] => {
        const { itemModel, iteration, isAddCommentRow } = entry;
        if (isAddCommentRow || !itemModel || (itemModel.isNew() && !itemModel.thread.threadContext)) {
            return [
                <TimelineSequence
                    className="activity-feed-sequence"
                    iconProps={{
                        render: () => {
                            return (
                                <Icon className="activity-feed-timeline-icon margin-vertical-4 secondary-text justify-center" iconName="CommentAdd" />
                            );
                        }
                    }}
                />,
                this.renderActivity(DiscussionIds.Comment, "row-add-comment", 0, typeof itemModel !== "string" ? itemModel : undefined)
            ];
        }

        const item = itemModel.thread;
        const threadType: DiscussionIds = this.getDiscussionId(item);
        // @ts-ignore TypeScript 4.0 upgrade
        if (threadType === "" || threadType === DiscussionIds.CodeAnalysis) {
            return [null, null];
        }
        if (threadType === DiscussionIds.Policy) {
            const policyType = getPropertyValue(item, "CodeReviewPolicyType");
            // Only one supported policy change
            if (policyType && policyType !== "fd2167ab-b0be-447a-8ec8-39368250530e") {
                return [null, null];
            }
        }

        let sequence: JSX.Element | undefined;
        let isDot = false;

        if (threadType === DiscussionIds.Comment || threadType === DiscussionIds.FileComment) {
            sequence = <Icon className="activity-feed-timeline-icon margin-vertical-4 secondary-text justify-center" iconName="Chat" />;
        } else if (threadType === DiscussionIds.Commit) {
            sequence = <Icon className="activity-feed-timeline-icon margin-vertical-4 secondary-text justify-center" iconName="BranchCommit" />;
        } else if (threadType === DiscussionIds.Iteration) {
            sequence = (
                <span className="activity-feed-timeline-iteration body-s font-weight-semibold text-center flex-self-stretch margin-vertical-4">
                    {iteration && iteration.id.toFixed()}
                </span>
            );
        }

        if (!sequence) {
            isDot = true;
            sequence = (
                <div className="flex-center justify-center margin-vertical-4">
                    <div className="activity-feed-timeline-dot" />
                </div>
            );
        }

        const rowKey = `row-${itemModel.id}`;

        return [
            <TimelineSequence
                className={css(
                    "activity-feed-sequence",
                    itemModel.isUnread().value && "new",
                    isDot && "dot",
                    this.selectedThreadId === item.id && "selected"
                )}
                iconProps={{
                    render: () => {
                        return sequence!;
                    }
                }}
            />,
            <ActivityFeedIntersectionRow rowIndex={rowIndex} rowKey={rowKey}>
                {this.renderActivity(threadType, rowKey, rowIndex, itemModel, iteration)}
            </ActivityFeedIntersectionRow>
        ];
    };

    private commentUpdated = (threadId: number) => {
        this.recalculateItemHeights[threadId] = true;
        if (this.itemHeights[threadId]) {
            this.itemHeights[threadId].value = 0;
        }
        this.tryUpdateTimelineHeights();
    };

    private tryUpdateTimelineHeights = () => {
        this.debouncedHeightChanges();
    };

    private invalidateHeights = () => {
        // Keep all the heights we've calculated, but mark them to be recalculated next time they are rendered.
        this.recalculateItemHeights = {};
        for (const item of this.items.value) {
            this.recalculateItemHeights[item.id] = true;
        }
    };

    private onResize = () => {
        this.invalidateHeights();
        this.tryUpdateTimelineHeights();
    };

    private updateRowHeight = (threadId: number, ref: React.RefObject<HTMLDivElement>) => {
        if (ref && ref.current) {
            const heights = this.itemHeights;
            const newHeight = ref.current.getBoundingClientRect().height;
            if (!heights[threadId]) {
                heights[threadId] = new ObservableValue<number>(newHeight);
                this.tryUpdateTimelineHeights();
            } else if (heights[threadId].value !== newHeight) {
                heights[threadId].value = newHeight;
                this.tryUpdateTimelineHeights();
            }
        }
    };

    private renderActivity = (
        threadType: DiscussionIds,
        rowKey: string,
        rowIndex: number,
        threadModel?: IGitPullRequestCommentThreadModel,
        iteration?: IIteration
    ) => {
        const activityFeedShimService = getActivityFeedShimService(this.context.pageContext);

        const shimState = activityFeedShimService.getShimState(rowKey, rowIndex);
        const itemHeightValue = threadModel ? this.itemHeights[threadModel.id] : 0;

        return (
            <Observer itemHeight={itemHeightValue} isShim={shimState}>
                {(props: { itemHeight: number; isShim: boolean | undefined }) => {
                    const { itemHeight, isShim } = props;
                    let style: React.CSSProperties;
                    let ref: React.RefObject<HTMLDivElement>;
                    if (!isShim && threadModel) {
                        if (!this.itemRefs[threadModel.id]) {
                            this.itemRefs[threadModel.id] = React.createRef<HTMLDivElement>();
                        }

                        ref = this.itemRefs[threadModel.id];

                        if (this.recalculateItemHeights[threadModel.id]) {
                            if (this.recalculateHandlers[threadModel.id]) {
                                window.clearTimeout(this.recalculateHandlers[threadModel.id]);
                            }

                            this.recalculateItemHeights[threadModel.id] = false;

                            this.recalculateHandlers[threadModel.id] = window.setTimeout(() => {
                                this.updateRowHeight(threadModel.id, ref);

                                // Measure twice, once to get an initial height in case the user is scrolling fast, and another time to get the height
                                // if the item is still around and has grown from images loading.
                                this.recalculateHandlers[threadModel.id] = window.setTimeout(() => {
                                    this.updateRowHeight(threadModel.id, ref);
                                }, 1500);
                            }, 500);
                        } else if (itemHeight) {
                            style = { minHeight: itemHeight };
                        }
                    }

                    return (
                        <ScreenSizeObserver>
                            {(screenSizeProps: { screenSize: ScreenSize }) => (
                                <div
                                    ref={ref}
                                    className={css(
                                        "flex-row flex-grow padding-bottom-16 scroll-hidden",
                                        screenSizeProps.screenSize !== ScreenSize.xsmall && "padding-left-8 padding-right-4"
                                    )}
                                    key={rowKey}
                                    style={style}
                                >
                                    <WrappedComponent
                                        wrappedProps={{
                                            iteration,
                                            isShim: isShim,
                                            isShimObservable: shimState,
                                            thread: threadModel ? threadModel.thread : undefined,
                                            threadModel,
                                            newInputRef: threadModel ? undefined : this.newCommentRef,
                                            onAddComment: threadModel && (() => this.commentUpdated(threadModel.id)),
                                            onCancelComment: threadModel && (() => this.commentUpdated(threadModel.id)),
                                            onDeleteComment: threadModel && (() => this.commentUpdated(threadModel.id)),
                                            onUpdateComment: threadModel && (() => this.commentUpdated(threadModel.id))
                                        }}
                                        wrappedType={`ms.vss-code-web.${threadType}`}
                                    />
                                </div>
                            )}
                        </ScreenSizeObserver>
                    );
                }}
            </Observer>
        );
    };

    private onItemsChange = () => {
        const items = this.items.value;

        // This gets called with all items whenever one changes.
        this.invalidateHeights();

        this.countFilterItems(items);
        this.updateItems(items);
        this.scrollToSelectedThread();
        return true;
    };

    private updateItems = (items: IGitPullRequestCommentThreadModel[]) => {
        const filterId = this.selectedFilterId.value;
        // First iteration is never visible in activity feed, it's considered part of PR creation
        const iterationsPool = this.iterations.value.slice(1);
        const entries = items.map(itemModel => {
            const threadType = this.getDiscussionId(itemModel.thread);
            let iteration: IIteration | undefined = undefined;
            if (threadType === DiscussionIds.Iteration) {
                // We don't have enough information to match threads with iterations. We do our best and extract in order
                // so if 2 iterations would match with 2 threads, they will be matched by date which is almost always correct.
                const index = iterationsPool.findIndex(iteration => this.doesIterationMatch(itemModel.thread, iteration));
                iteration = iterationsPool.splice(index, 1)[0];
            }

            return { itemModel, iteration } as ActivityEntry;
        });

        const reversedEntries = entries.reverse();

        const canAddComments = hasPermission(this.context.pageContext, PullRequestPermissions.AddEditComment);

        if (canAddComments && !reversedEntries.filter(({ itemModel }) => !itemModel || (itemModel.isNew() && !itemModel.thread.threadContext))[0]) {
            reversedEntries.unshift({ isAddCommentRow: true });
        }

        if (filterId === FilterIds.Everything) {
            this.filteredItems.value = reversedEntries;
        } else {
            const filteredItems: ActivityEntry[] = [];

            reversedEntries.forEach(entry => {
                const { itemModel, isAddCommentRow } = entry;
                if (isAddCommentRow || !itemModel || itemModel.id < 0) {
                    filteredItems.push(entry);
                    return;
                }

                const item = itemModel.thread;
                if (filterId === FilterIds.New) {
                    itemModel.isUnread().value && filteredItems.push(entry);
                } else if (filterId === FilterIds.MyComments) {
                    this.isThreadMine(item) && filteredItems.push(entry);
                } else {
                    const threadType: DiscussionIds = this.getDiscussionId(item);
                    if (threadType === DiscussionIds.Comment || threadType === DiscussionIds.FileComment) {
                        if (filterId === FilterIds.ActiveComments) {
                            if (item.status === CommentThreadStatus.Active || item.status === CommentThreadStatus.Pending) {
                                filteredItems.push(entry);
                            }
                        } else if (filterId === FilterIds.ResolvedComments) {
                            if (
                                item.status === CommentThreadStatus.Fixed ||
                                item.status === CommentThreadStatus.WontFix ||
                                item.status === CommentThreadStatus.Closed ||
                                item.status === CommentThreadStatus.ByDesign
                            ) {
                                filteredItems.push(entry);
                            }
                        } else {
                            filteredItems.push(entry);
                        }
                    }
                }
            });

            this.filteredItems.value = filteredItems;
        }
    };

    private onThreadFilterChange = (ev: any, item: IListBoxItem) => {
        this.selectedFilterId.value = item.id;
        const items = this.items.value;
        getActivityFeedShimService(this.context.pageContext).reset();
        this.updateItems(items);

        // Make sure activity feed filter is remembered between the tabs
        const pullRequestViewService = this.context.pageContext.getService<IPullRequestViewService>("IPullRequestViewService");
        pullRequestViewService.setActivityFeedFilter(item.id);
    };

    private countFilterItems = (items: IGitPullRequestCommentThreadModel[]) => {
        // 0: Everything, 1: Comments, 2: New, 3: Mine, 4: Active Comments, 5: Resolved Comments
        const filterNumbers = [items.length, 0, 0, 0, 0, 0];

        items.forEach((itemModel: IGitPullRequestCommentThreadModel) => {
            const item = itemModel.thread;
            const threadType: DiscussionIds = this.getDiscussionId(item);

            if (itemModel.isUnread().value) {
                filterNumbers[2]++;
            }
            if (this.isThreadMine(item)) {
                filterNumbers[3]++;
            }
            if (threadType === DiscussionIds.Comment || threadType === DiscussionIds.FileComment) {
                filterNumbers[1]++;
                if (item.status === CommentThreadStatus.Active || item.status === CommentThreadStatus.Pending) {
                    filterNumbers[4]++;
                } else {
                    filterNumbers[5]++;
                }
            }
        });

        this.filterItems.value = this.filters.map((filter: { id: string; format: string }, index: number) => {
            return { id: filter.id, text: format(filter.format, filterNumbers[index]) };
        });
    };

    private getDiscussionId = (thread: GitPullRequestCommentThread): DiscussionIds => {
        let threadType: DiscussionIds = DiscussionIds.Unknown;

        if (!thread) {
            return threadType;
        }

        // label overall thread type
        if (thread.properties && thread.properties.CodeReviewThreadType) {
            const threadTypeString = getPropertyValue(thread, "CodeReviewThreadType");
            if (localeIgnoreCaseComparer(threadTypeString, SystemDiscussionIds.MERGEATTEMPT) === 0) {
                return DiscussionIds.Merge;
            } else if (localeIgnoreCaseComparer(threadTypeString, SystemDiscussionIds.REFUPDATE) === 0 ||
                localeIgnoreCaseComparer(threadTypeString, SystemDiscussionIds.CONFLICTSRESOLVED) === 0) {
                return this.supportsIterations ? DiscussionIds.Iteration : DiscussionIds.Commit;
            } else if (localeIgnoreCaseComparer(threadTypeString, SystemDiscussionIds.VOTEUPDATE) === 0) {
                return DiscussionIds.Vote;
            } else if (localeIgnoreCaseComparer(threadTypeString, SystemDiscussionIds.RESETALLVOTES) === 0) {
                return DiscussionIds.ResetAllVotes;
            } else if (localeIgnoreCaseComparer(threadTypeString, SystemDiscussionIds.RESETMULTIPLEVOTES) === 0) {
                return DiscussionIds.ResetMultipleVotes;
            } else if (localeIgnoreCaseComparer(threadTypeString, SystemDiscussionIds.STATUSUPDATE) === 0) {
                return DiscussionIds.PREvent;
            } else if (localeIgnoreCaseComparer(threadTypeString, SystemDiscussionIds.POLICYSTATUSUPDATE) === 0) {
                return DiscussionIds.Policy;
            } else if (localeIgnoreCaseComparer(threadTypeString, SystemDiscussionIds.CODEANALYSISISSUE) === 0) {
                return DiscussionIds.CodeAnalysis;
            } else if (localeIgnoreCaseComparer(threadTypeString, SystemDiscussionIds.REVIEWERSUPDATE) === 0) {
                return DiscussionIds.Reviewer;
            } else if (localeIgnoreCaseComparer(threadTypeString, SystemDiscussionIds.AUTOCOMPLETEUPDATE) === 0) {
                return DiscussionIds.AutoComplete;
            } else if (localeIgnoreCaseComparer(threadTypeString, SystemDiscussionIds.ISDRAFTUPDATE) === 0) {
                return DiscussionIds.IsDraft;
            } else if (localeIgnoreCaseComparer(threadTypeString, SystemDiscussionIds.TARGETCHANGED) === 0) {
                return DiscussionIds.Iteration;
            } else if (localeIgnoreCaseComparer(threadTypeString, SystemDiscussionIds.BROKENSTATE) === 0) {
                return DiscussionIds.BrokenState;
            } else if (localeIgnoreCaseComparer(threadTypeString, SystemDiscussionIds.SOURCEBRANCHCHANGED) === 0) {
                return DiscussionIds.SourceBranchChanged;
            }
        } else if (thread.threadContext && thread.threadContext.filePath) {
            threadType = DiscussionIds.FileComment;
        } else if (thread.comments && thread.comments.length > 0) {
            threadType = DiscussionIds.Comment;
        }

        return threadType;
    };

    private isThreadMine = (thread: GitPullRequestCommentThread): boolean => {
        if (this.currentUserId && thread.comments && thread.comments.length > 0) {
            for (const comment of thread.comments) {
                if (!comment.isDeleted && comment.author && comment.author.id === this.currentUserId) {
                    return true;
                }
            }
        }
        return false;
    };

    private doesIterationMatch = (thread: GitPullRequestCommentThread, iteration: IIteration): boolean => {
        const isThreadRetarget = equals(getPropertyValue(thread, "CodeReviewThreadType"), SystemDiscussionIds.TARGETCHANGED, true);
        const isIterationRetarget = (iteration.reason & IterationReason.Retarget) > 0;
        if (isThreadRetarget === isIterationRetarget) {
            // When it's a retarget, CodeReviewRefNewHeadCommit is not present so nothing else to check.
            return true;
        }

        const threadHeadCommitId = getPropertyValue(thread, "CodeReviewRefNewHeadCommit");
        const iterationHeadCommitId = iteration.commits[0] && iteration.commits[0].commit && iteration.commits[0].commit.commitId;

        // An iteration without commits should match with any thread. This happens when a rebase moves the ref backwards
        return !iterationHeadCommitId || threadHeadCommitId === iterationHeadCommitId;
    };
}
